// driverLocations — driver GPS that is live ONLY while a map is open
// (FEATURES.liveV2Locations, migration 0077).
//
// Before: every tab of every role re-read the whole `users` table every 30 s,
// GPS blob included, and the Delivery Map picked the positions out of it — so
// the map lagged up to 30 s and everybody paid for data only the map uses.
// Now, while at least one map is mounted:
//   1. TRUTH   — the `driver_locations_snapshot` RPC (role-gated in the database)
//                is read on open, after any channel re-join, when the network
//                comes back, when the tab becomes visible again, and on a slow
//                safety timer;
//   2. HINTS   — the private Broadcast topic `team:locations` carries
//                `driver_location {driver_id, at, loc}` the moment a phone writes
//                a position. `at` is the SERVER clock; an event that is not newer
//                than what is held for that driver is ignored, so duplicates and
//                out-of-order deliveries cannot move a marker backwards.
// A broadcast is only ever a hint: with every event lost the map is still right
// within one safety read.
//
// Fallbacks, all silent (a background read never surfaces an error):
//   • channel refused / failed  → the snapshot is polled every 30 s (today's lag);
//   • snapshot RPC missing      → `users (id, last_location)` is polled directly,
//                                 which is exactly the data the map gets today.
import { useSyncExternalStore } from 'react';
import type { User } from '../../types';
import { supabase } from '../supabaseClient';
import { joinScopedChannel } from './scopedChannel';

export type DriverLocation = NonNullable<User['lastLocation']>;
export type DriverLocationMap = Record<string, DriverLocation>;

export const LOCATIONS_TOPIC = 'team:locations';
export const LOCATION_EVENT = 'driver_location';
/** Poll interval when the channel is not available (same as today's users poll). */
export const LOCATIONS_POLL_MS = 30000;
/** Safety re-read while the channel IS live — heals any lost hint. */
export const LOCATIONS_SAFETY_MS = 120000;
// The trigger stamps `at` inside the phone's transaction, a moment BEFORE it
// commits. A snapshot taken in that moment does not contain the new position
// but would carry a later clock, and the event would then look stale. Dating
// the snapshot a little early closes that window; the only cost is that an
// event already contained in the snapshot may be applied again (same data).
export const SNAPSHOT_SLACK_MS = 5000;

/** `users` columns for the app-wide roster read when GPS no longer rides on it:
 *  everything the table has EXCEPT `last_location`. A column added to `users` must be added
 *  here too, or every site with liveV2Locations on silently never sees it (store_access, 0084,
 *  was missed at first: drivers without access looked assignable on staging). On a database
 *  that lacks a named column the caller falls back to `select *` (AppContext, 42703). */
export const USERS_SELECT_WITHOUT_GPS =
  'id, auth_id, name, role, email, phone, address, requires_password_reset, is_archived, ' +
  'temp_packer_driver_id, twilio_number, is_on_duty, last_opened_at, created_at, placeholder, store_access, ' +
  // 0090 (UPE-119): the GPS rule, the shift flag and the "GPS on today" stamp.
  'gps_exempt, out_of_shift, out_of_shift_at, out_of_shift_by, gps_on_at';

const EMPTY: DriverLocationMap = Object.freeze({}) as DriverLocationMap;
let current: DriverLocationMap = EMPTY;
// Server clock (ms) of what is held per driver — locations AND clears.
const heldAt = new Map<string, number>();
const listeners = new Set<() => void>();

let refs = 0;
let stopRun: (() => void) | null = null;

const publish = (next: DriverLocationMap) => { current = next; listeners.forEach((l) => l()); };

const validLoc = (v: any): v is DriverLocation =>
  !!v && typeof v === 'object' && typeof v.lat === 'number' && typeof v.lng === 'number';

const isMissingFunction = (e: any): boolean =>
  e?.code === 'PGRST202' || e?.code === '42883' ||
  /could not find the function|does not exist/i.test(String(e?.message || ''));

/** Apply one `driver_location` event. Exported for tests. */
export function applyLocationEvent(payload: any): void {
  const id = payload?.driver_id == null ? '' : String(payload.driver_id);
  const at = Number(payload?.at);
  if (!id || !Number.isFinite(at)) return;
  if (at <= (heldAt.get(id) ?? -Infinity)) return; // duplicate or older than what is held
  heldAt.set(id, at);
  if (validLoc(payload.loc)) { publish({ ...current, [id]: payload.loc }); return; }
  if (id in current) { const { [id]: _gone, ...rest } = current; publish(rest); }
}

/** Apply a full snapshot read at server time `atMs`. A driver whose held state
 *  is newer than the snapshot keeps it; a driver absent from the snapshot is
 *  removed unless an event newer than the snapshot put it there. */
function applySnapshot(rows: Array<{ id: string; loc: any }>, atMs: number): void {
  const next: DriverLocationMap = {};
  const seen = new Set<string>();
  let changed = false;
  for (const r of rows) {
    if (!r.id || !validLoc(r.loc)) continue;
    seen.add(r.id);
    if ((heldAt.get(r.id) ?? -Infinity) > atMs) {
      if (current[r.id]) next[r.id] = current[r.id];
      continue;
    }
    heldAt.set(r.id, atMs);
    const prev = current[r.id];
    if (prev && JSON.stringify(prev) === JSON.stringify(r.loc)) next[r.id] = prev;
    else { next[r.id] = r.loc; changed = true; }
  }
  for (const id of Object.keys(current)) {
    if (seen.has(id)) continue;
    if ((heldAt.get(id) ?? -Infinity) > atMs) next[id] = current[id];
    else { heldAt.set(id, atMs); changed = true; }
  }
  if (changed || Object.keys(next).length !== Object.keys(current).length) publish(next);
}

function run(): () => void {
  let dead = false;
  let channelLive = false;
  let useTable = false;        // snapshot RPC missing → read users directly
  let loading = false;
  let again = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let soon: ReturnType<typeof setTimeout> | null = null;
  const hidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

  const load = async (): Promise<void> => {
    if (dead) return;
    if (loading) { again = true; return; }
    loading = true;
    try {
      if (!useTable) {
        const { data, error } = await supabase().rpc('driver_locations_snapshot');
        if (dead) return;
        if (error) {
          if (!isMissingFunction(error)) return; // transient / refused — next trigger retries
          useTable = true;
        } else {
          // { at_ms, rows: [{ driver_id, loc }] } — at_ms is the SERVER clock.
          const at = Number((data as any)?.at_ms);
          const rows = Array.isArray((data as any)?.rows) ? (data as any).rows : [];
          if (!Number.isFinite(at)) return;
          applySnapshot(rows.map((r: any) => ({ id: String(r?.driver_id ?? ''), loc: r?.loc })), at - SNAPSHOT_SLACK_MS);
          return;
        }
      }
      // Pre-migration: the same data the app-wide users poll carries today.
      const { data, error } = await supabase().from('users').select('id, last_location').not('last_location', 'is', null);
      if (dead || error) return;
      // No server clock on this path, and the browser clock is never compared
      // with the server one: date the read at the newest server time seen.
      let newest = 0;
      heldAt.forEach((v) => { if (v > newest) newest = v; });
      applySnapshot(((data as any[]) || []).map((r) => ({ id: String(r.id), loc: r.last_location })), newest);
    } catch {
      /* background read — never surfaces */
    } finally {
      loading = false;
      if (again && !dead) { again = false; void load(); }
    }
  };

  const arm = () => {
    if (timer) clearTimeout(timer);
    const every = channelLive ? LOCATIONS_SAFETY_MS : LOCATIONS_POLL_MS;
    timer = setTimeout(() => { if (dead) return; if (!hidden()) void load(); arm(); }, every * (0.8 + Math.random() * 0.4));
  };
  // Small random delay: an office coming back online together must not read in step.
  const loadSoon = () => {
    if (dead || soon) return;
    soon = setTimeout(() => { soon = null; void load(); }, 200 + Math.random() * 1800);
  };
  const onVisible = () => { if (!hidden()) loadSoon(); };

  void load();
  arm();
  const leave = joinScopedChannel(LOCATIONS_TOPIC, [LOCATION_EVENT], {
    onEvent: (_event, payload) => { if (!dead) applyLocationEvent(payload); },
    onLive: () => { channelLive = true; arm(); },
    onResync: () => { void load(); },          // already jittered by channelResync
    onUnavailable: () => { channelLive = false; arm(); },
  });
  if (typeof window !== 'undefined') window.addEventListener('online', loadSoon);
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible);

  return () => {
    dead = true;
    if (timer) clearTimeout(timer);
    if (soon) clearTimeout(soon);
    if (typeof window !== 'undefined') window.removeEventListener('online', loadSoon);
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible);
    leave();
  };
}

/** Ref-counted: the first watcher starts the snapshot + channel, the last one
 *  leaves the channel and forgets the positions. */
export function watchDriverLocations(onChange: () => void): () => void {
  listeners.add(onChange);
  refs += 1;
  if (refs === 1) stopRun = run();
  let left = false;
  return () => {
    if (left) return;
    left = true;
    listeners.delete(onChange);
    refs -= 1;
    if (refs === 0) {
      stopRun?.();
      stopRun = null;
      current = EMPTY;
      heldAt.clear();
    }
  };
}

export const getDriverLocations = (): DriverLocationMap => current;

const noopSubscribe = () => () => {};
const readEmpty = () => EMPTY;

/** Positions for a map. `enabled = false` (flag off) does nothing at all. */
export function useLiveDriverLocations(enabled: boolean): DriverLocationMap {
  return useSyncExternalStore(
    enabled ? watchDriverLocations : noopSubscribe,
    enabled ? getDriverLocations : readEmpty,
    enabled ? getDriverLocations : readEmpty,
  );
}

/** Test seam. */
export function _resetDriverLocations(): void {
  stopRun?.();
  stopRun = null;
  refs = 0;
  current = EMPTY;
  heldAt.clear();
  listeners.clear();
}
